import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AlbumsApiService } from '../../services/albums.api.service';
import * as AlbumsActions from './albums.actions';
import { selectAlbumsPage, selectAlbumsState } from './albums.selectors';

@Injectable()
export class AlbumsEffects {
  private actions$ = inject(Actions);
  private albumsApiService = inject(AlbumsApiService);
  private store = inject(Store);

  loadAlbums$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.loadAlbums),
      withLatestFrom(this.store.select(selectAlbumsState)),
      switchMap(([ { params }, state ]) =>
        this.albumsApiService
          .findAll({
            ...state.query,
            ...params,
            page: 1,
            limit: params?.limit ?? state.limit,
            sortBy: params?.sortBy ?? state.query.sortBy ?? 'eventDate',
            sortOrder: params?.sortOrder ?? state.query.sortOrder ?? 'DESC',
          })
          .pipe(
            map((response) => AlbumsActions.loadAlbumsSuccess({ response })),
            catchError((error) =>
              of(AlbumsActions.loadAlbumsFailure({ error: error.message || 'Failed to load albums' })),
            ),
          ),
      ),
    ),
  );

  loadMoreAlbums$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.loadMoreAlbums),
      withLatestFrom(this.store.select(selectAlbumsPage), this.store.select(selectAlbumsState)),
      switchMap(([ , currentPage, state ]) =>
        this.albumsApiService
          .findAll({
            ...state.query,
            page: currentPage + 1,
          })
          .pipe(
            map((response) => AlbumsActions.loadMoreAlbumsSuccess({ response })),
            catchError((error) =>
              of(AlbumsActions.loadMoreAlbumsFailure({ error: error.message || 'Failed to load more albums' })),
            ),
          ),
      ),
    ),
  );

  loadAlbumBySlug$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.loadAlbumBySlug),
      switchMap(({ slug }) =>
        this.albumsApiService.findOneBySlug(slug).pipe(
          map((album) => AlbumsActions.loadAlbumSuccess({ album })),
          catchError((error) =>
            of(AlbumsActions.loadAlbumFailure({ error: error.message || 'Failed to load album' })),
          ),
        ),
      ),
    ),
  );

  loadAlbumById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.loadAlbumById),
      switchMap(({ id }) =>
        this.albumsApiService.findOneById(id).pipe(
          map((album) => AlbumsActions.loadAlbumSuccess({ album })),
          catchError((error) =>
            of(AlbumsActions.loadAlbumFailure({ error: error.message || 'Failed to load album' })),
          ),
        ),
      ),
    ),
  );
}
